import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ShieldCheck, Clock, Wallet } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/employee"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(identifier, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/employee");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-zinc-50" data-testid="login-page">
      {/* Form side */}
      <div className="flex flex-col justify-between p-6 sm:p-10 lg:p-14 bg-white relative overflow-hidden min-h-screen lg:min-h-0">
        {/* Top brand */}
        <div className="flex items-center gap-2 z-10">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-orange-400 flex items-center justify-center shadow-md">
            <Sparkles size={16} className="text-white" />
          </div>
          <div>
            <div className="font-bold text-zinc-900 dark:text-zinc-100 text-sm leading-tight">EMS<span className="text-orange-400">.</span></div>
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400 font-semibold uppercase tracking-[0.16em]">Sanju Animations IT Solutions</div>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-md w-full mx-auto z-10 py-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold mb-6">
            <ShieldCheck size={12} /> Secure access
          </div>
          <h1 className="rule-text text-4xl sm:text-5xl text-zinc-900 dark:text-zinc-100 mb-3 leading-tight">
            Welcome back.
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-8 leading-relaxed">
            Sign in to manage attendance, payroll, and your team with one elegant console.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="identifier" className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">Email or Username</Label>
              <Input
                id="identifier"
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className="rounded-lg border-zinc-200 dark:border-zinc-800 h-11 focus-visible:ring-indigo-500 focus-visible:ring-offset-0"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-lg border-zinc-200 dark:border-zinc-800 h-11 focus-visible:ring-indigo-500 focus-visible:ring-offset-0"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-lg klein-btn font-semibold text-sm group"
            >
              {loading ? "Signing in..." : (
                <span className="inline-flex items-center gap-2">Sign in <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></span>
              )}
            </Button>
          </form>

        </div>

        <div className="text-xs text-zinc-400 z-10">© {new Date().getFullYear()} · Sanju Animations IT Solutions · v2.0</div>

        {/* Soft decorative blob */}
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-orange-100 opacity-40 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-indigo-100 opacity-50 blur-3xl"></div>
      </div>

      {/* Visual side (hidden on mobile) */}
      <div className="hidden lg:block relative bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-60"></div>

        {/* Decorative SVG vectors */}
        <svg className="absolute -top-20 -right-20 w-96 h-96 text-orange-300/40" viewBox="0 0 200 200" fill="currentColor" aria-hidden="true">
          <path d="M44.6,-67.1C58.2,-58.2,69.7,-46.1,75.1,-31.8C80.5,-17.6,79.7,-1.1,76.4,14.3C73.2,29.7,67.4,44,57.1,55.2C46.8,66.4,32,74.5,15.6,77.4C-0.8,80.3,-18.7,78,-33.3,70.4C-47.9,62.9,-59.1,50.2,-67.5,35.5C-75.9,20.8,-81.5,4.1,-79.6,-11.7C-77.7,-27.5,-68.4,-42.5,-55.6,-52.1C-42.8,-61.8,-26.6,-66,-10.4,-69.7C5.7,-73.4,11.4,-76.7,23.1,-76.7C34.8,-76.7,52.5,-73.4,62.7,-67.1Z" transform="translate(100 100)" />
        </svg>
        <svg className="absolute bottom-10 -left-10 w-80 h-80 text-white/10" viewBox="0 0 200 200" fill="currentColor" aria-hidden="true">
          <path d="M37.4,-58.2C49.5,-50.6,60.9,-41.4,68.3,-29.3C75.7,-17.2,79,-2.3,76.4,11.6C73.8,25.5,65.2,38.4,53.7,47.2C42.2,56,27.7,60.6,12.5,64.3C-2.7,68,-18.7,70.7,-32.6,66.2C-46.5,61.7,-58.4,49.9,-65.5,36C-72.6,22.1,-74.9,6,-72.5,-9.1C-70.1,-24.2,-63,-38.4,-51.7,-47C-40.4,-55.7,-24.9,-58.7,-10.6,-58C3.7,-57.3,25.4,-65.7,37.4,-58.2Z" transform="translate(100 100)" />
        </svg>

        {/* Geometric grid lines */}
        <svg className="absolute inset-0 w-full h-full opacity-20" aria-hidden="true">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Content */}
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="inline-flex items-center gap-2 self-start px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-300"></span> All-in-one HR
          </div>

          <div className="space-y-8">
            <h2 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100 xl:text-6xl leading-[1.05] max-w-md">
              The calmest way to <span className="text-orange-300">run payroll</span>.
            </h2>
            <p className="max-w-sm text-base text-white/80 leading-relaxed">
              Track attendance, approve bills, transfer salaries, and notify employees on WhatsApp — from a single quiet console.
            </p>

            {/* Mini feature pills */}
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                { icon: Clock, label: "Attendance" },
                { icon: Wallet, label: "Payroll" },
                { icon: Sparkles, label: "WhatsApp" },
              ].map((f) => (
                <div key={f.label} className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 p-4 text-center">
                  <f.icon size={18} className="mx-auto text-orange-300 mb-2" />
                  <div className="text-xs font-semibold">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs text-white/60">
            <div className="flex -space-x-2">
              <div className="w-7 h-7 rounded-full bg-orange-400 border-2 border-indigo-700"></div>
              <div className="w-7 h-7 rounded-full bg-emerald-400 border-2 border-indigo-700"></div>
              <div className="w-7 h-7 rounded-full bg-purple-400 border-2 border-indigo-700"></div>
            </div>
            Trusted by your team every day.
          </div>
        </div>
      </div>
    </div>
  );
}
